import ProfilePicture from "@/components/common/profile-picture";
import { ErrorLabel, Input, Label, MultiSelectDropdown } from "@/components/ui";
import { SelectDropdown } from "@/components/ui/select-dropdown";
import { LinkedInIcon } from "@/icons";
import {
	BASE_URL,
	CATEGORIES_V1_API,
	PRIVATE_DISCUSSION_BOARD_PATH,
	USER_PROFILE_API,
	countryISOs,
} from "@/lib/constants";
import { fileValidation, formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { Category } from "@prisma/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign, BuildingIcon, GlobeIcon, InfoIcon, Loader2Icon, TwitterIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import { z } from "zod";
import { TProfileUpdateFormData } from "../update-profile";
import CongratulationsModal from "./congratulations-modal";
import GetVerified from "./get-verified";
import VerificationCard from "./verification-card";
import confetti from "canvas-confetti";

const socialLinksSchema = z.object({
	personalWebsite: z.string().optional().nullable(),
	linkedin: z.string().min(10, { message: "Please provide a valid LinkedIn profile link" }),
	instagram: z.string().optional().nullable(),
	twitter: z.string().optional().nullable(),
});

const profileUpdateSchema = z.object({
	profilePicture: fileValidation({ isRequired: false }),
	name: z
		.string()
		.min(3, { message: "Please enter both your first name and last name." })
		.max(50, { message: "Name cannot exceed 50 characters" })
		.refine(
			(val) =>
				val
					.trim()
					.split(/\s+/)
					.every((part) => part.length <= 20),
			{
				message: "Each part of the name cannot exceed 20 characters",
			}
		),
	username: z.string().optional().nullable(),
	bio: z
		.string()
		.min(4, { message: "Please enter your bio to proceed." })
		.max(2000, { message: "Bio cannot exceed 2000 characters" }),
	jobTitle: z.string().nullable(),
	company: z.string().min(3, { message: "Please enter your company name" }).nullable(),
	socialLinks: socialLinksSchema,
});

export type TProfileUpdateSchema = z.infer<typeof profileUpdateSchema>;

async function getCategoriesData() {
	const res = await fetch(CATEGORIES_V1_API);
	if (!res.ok) {
		const { message } = await res.json();
		throw new Error(message);
	}

	const { data } = (await res.json()) as { data: Category[] };

	return data;
}

const howDoYouIdentifyYourselfOptions = [
	{ id: 1, name: "Proptech Co/Founder/CXO/" },
	{ id: 2, name: "Developers/Builder Co/Founder/CXO" },
	{ id: 3, name: "Business owner Co/Founder/CXO" },
	{ id: 4, name: "Professionals" },
	{ id: 5, name: "Proptech/Real Estate enthusiast" },
	{ id: 6, name: "PropTech Investors/VC" },
	{ id: 7, name: "Real Estate Investors/Buyers" },
	{ id: 8, name: "Real Estate Brokers/Agents" },
	{ id: 9, name: "Landlord/Asset Owners" },
	{ id: 10, name: "MultiFamily/SingleFamily Operators" },
	{ id: 11, name: "Proptech//RealEstate Consultants" },
	{ id: 12, name: "Lender/Mortgage/Banker" },
	{ id: 13, name: "Facilities/Industry Owners" },
	{ id: 14, name: "Others" },
];

const UpdateProfileForm = ({
	prefillProfileData,
}: {
	prefillProfileData: TProfileUpdateFormData;
}) => {
	const searchParams = useSearchParams();

	// Updates the button inside congratulations modal to redirect to specified destination when needed
	const id = searchParams.get("id");
	const from = searchParams.get("from");
	const type = searchParams.get("type");

	let congratulationsModalLink = undefined;
	let congratulationsModalLinkText = undefined;

	if (from === "private-discussion-board" && id) {
		congratulationsModalLink = `${PRIVATE_DISCUSSION_BOARD_PATH}/${id}`;
		congratulationsModalLinkText = `Go to ${id.toUpperCase()}`;
	}

	const { updateSession, user } = useAuth();
	const { closeUpdateProfileModal, openVIPUpgradeModal } = useModal();
	const username = user?.username;
	const isPreviouslyVerified = user?.isVerified;
	const isVIP = user?.isFounder || false;
	const profileImageUrl = formatImageUrl(prefillProfileData.profilePicture);
	const [selectedCategories, setSelectedCategories] = useState<string[]>(
		prefillProfileData?.categories || []
	);

	const [howDoYouIdentifyYourself, setHowDoYouIdentifyYourself] = useState<string>(
		prefillProfileData?.howDoYouIdentifyYourself || howDoYouIdentifyYourselfOptions[0].name
	);
	const [selectedLocation, setSelectedLocation] = useState<string>(
		prefillProfileData.location || ""
	);
	const [showCongratulationsModal, setShowCongratulationsModal] = useState<boolean>(false);

	// Loading States
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// Errors
	const [profilePictureError, setProfilePictureError] = useState<boolean>(false);
	const [howDoYouIdentifyYourselfError, setHowDoYouIdentifyYourselfError] =
		useState<boolean>(false);
	const [showCategoriesError, setShowCategoriesError] = useState<boolean>(false);
	const [showLocationError, setShowLocationError] = useState<boolean>(false);

	const queryClient = useQueryClient();
	const { data: categories = [] } = useQuery({
		queryKey: ["categories"],
		queryFn: getCategoriesData,
	});

	const sortedCategories = categories.sort((a, b) => a.name.localeCompare(b.name));
	const countries = Object.entries(countryISOs).map(([countryName, countryISO]) => ({
		id: countryISO,
		name: countryName,
	}));

	const {
		register,
		handleSubmit,
		getValues,
		formState: { errors },
		setValue,
		watch,
	} = useForm<TProfileUpdateSchema>({
		resolver: zodResolver(profileUpdateSchema),
		defaultValues: prefillProfileData,
		mode: "onChange",
	});

	const uploadToS3 = async (file: File, fileExtension: string) => {
		const key = `profile-pictures/${uuid()}.${fileExtension}`;
		const apiURL = new URL(`${BASE_URL}/api/presign`);
		apiURL.searchParams.set("key", key);

		try {
			const presignUrlResponse = await fetch(apiURL);
			const { objectPath, preSignedUrl } = await presignUrlResponse.json();

			await fetch(preSignedUrl, {
				method: "PUT",
				body: file,
			});

			return objectPath;
		} catch (error) {
			toast.error("Something went wrong during file upload");
			console.log(error);
		}
	};

	const handleConfetti = () => {
		confetti({
			particleCount: 100,
			spread: 70,
			origin: { y: 0.6 },
		});
	};

	const onSubmit: SubmitHandler<TProfileUpdateSchema> = async (data) => {
		try {
			setIsLoading(true);
			if (selectedCategories.length === 0) {
				setShowCategoriesError(true);
				return;
			}
			if (selectedLocation === "") {
				setShowLocationError(true);
				return;
			}
			if (howDoYouIdentifyYourself === "") {
				setHowDoYouIdentifyYourselfError(true);
				return;
			}
			if (!prefillProfileData.profilePicture && !data?.profilePicture?.[0]) {
				setProfilePictureError(true);
				return;
			}

			const file = data.profilePicture?.[0];
			const fileExtension = file?.name?.split(".").pop();
			let profilePicture = prefillProfileData?.profilePicture;
			if (file && fileExtension) {
				const objectPath = await uploadToS3(file, fileExtension);
				profilePicture = objectPath;
			}

			const formattedBody = {
				profilePicture: profilePicture,
				name: data.name,
				bio: data.bio,
				jobTitle: data.jobTitle,
				location: selectedLocation,
				company: data.company,

				categories: selectedCategories,
				howDoYouIdentifyYourself: howDoYouIdentifyYourself,

				// social links
				linkedin: data.socialLinks.linkedin,
				twitter: data.socialLinks.twitter,
				personalWebsite: data.socialLinks.personalWebsite,
				instagram: data.socialLinks.instagram,
			};

			const response = await fetch(USER_PROFILE_API, {
				method: "PATCH",
				body: JSON.stringify(formattedBody),
			});
			const { message, isVerified } = await response.json();

			if (response.ok) {
				const updatedSessionObject = {
					isVerified: isVerified,
					image: profilePicture,
					username: getValues("username"),
					name: getValues("name"),
				};

				closeUpdateProfileModal();

				if (!isPreviouslyVerified && isVerified) {
					// Analytics event to track user verifications
					window.rudderanalytics?.track("New Verified User", {
						name: updatedSessionObject?.name,
						email: user?.email,
						username: updatedSessionObject?.username,
					});

					handleConfetti();
					setShowCongratulationsModal(true);
				} else {
					toast.success(message);
				}

				await updateSession(updatedSessionObject);
				queryClient.invalidateQueries({ queryKey: ["updateProfileDetails"] });
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	let profilePicture;
	const uploadedProfilePicture = getValues("profilePicture");
	if (uploadedProfilePicture instanceof FileList) {
		profilePicture = URL.createObjectURL(uploadedProfilePicture?.[0]);
	} else {
		profilePicture = profileImageUrl;
	}

	useEffect(() => {
		if (type === "vip-account-upgrade") {
			openVIPUpgradeModal();
		}
	}, [type, openVIPUpgradeModal]);

	return (
		<div className="mx-auto flex w-11/12 max-w-5xl flex-wrap items-start justify-between gap-4 rounded-lg bg-white px-4 md:w-full md:flex-nowrap md:px-8 md:py-4">
			<div className="basis-full py-8 md:basis-1/3">
				<div className="rounded-lg bg-gray-100 py-8 text-center">
					<ProfilePicture
						profilePictureUrl={profilePicture}
						alternateText={user?.name}
						className={{
							container: "mx-auto mb-8 h-32 w-32 rounded-full",
							image: "rounded-full fill-gray-500",
						}}
						isVerified={user?.isVerified}
						isFounder={user?.isFounder}
					/>
					<h3 className="mb-6 text-xl font-semibold capitalize">
						{getValues("name")
							.trim()
							.split(/\s+/)
							.some((part) => part.length > 20)
							? getValues("name")
									.match(/.{1,20}/g)
									?.join("\n")
							: getValues("name")}
						{/* If more than 20 characters and no spaces, split the name into multiple lines to prevent UI breaks*/}
					</h3>
					<label
						htmlFor="image"
						className="mx-auto flex w-fit items-center justify-center gap-x-2 rounded-lg bg-white px-8 py-3 text-sm font-semibold hover:cursor-pointer"
					>
						<input
							type="file"
							id="image"
							accept="image/*"
							className="hidden"
							{...register("profilePicture")}
						/>

						{prefillProfileData.profilePicture ? "Update Profile Picture" : "Upload a Picture"}
					</label>

					{(errors?.profilePicture?.message || profilePictureError) && (
						<ErrorLabel>
							{errors?.profilePicture?.message?.toString() || "Please provide a valid image file"}
						</ErrorLabel>
					)}
				</div>

				<VerificationCard />

				<GetVerified
					title={isPreviouslyVerified ? "Verified" : "Get Verified!"}
					profilePicture={watch("profilePicture") || profileImageUrl}
					name={watch("name")}
					bio={watch("bio")}
					categories={selectedCategories.length}
					identification={howDoYouIdentifyYourself}
					location={selectedLocation}
					company={watch("company")}
					socialLinks={watch("socialLinks.linkedin") ? true : false}
				/>
			</div>

			<div className="px-6 py-8 md:basis-2/3">
				<div className="mb-3 border-b pb-2">
					<h2 className="text-lg font-semibold tracking-tight text-gray-900 md:text-xl">
						Update Profile
					</h2>
				</div>

				<form onSubmit={handleSubmit(onSubmit)}>
					<Input
						id="name"
						label="Name"
						placeholder="Your Name"
						value={watch("name")}
						{...register("name")}
						onChange={(e) => {
							if (
								e.target.value
									.trim()
									.split(/\s+/)
									.every((part) => part.length <= 21)
							) {
								setValue("name", e.target.value, { shouldValidate: true });
							}
						}}
						classNames={{ container: "mb-5 sm:max-w-full" }}
						message={errors?.name?.message}
						maxLength={51} //Intentionally set to 51 & 21 to show error message
					/>

					<div className="mb-5">
						<Label
							showTooltip={!getValues("username")}
							tooltipTrigger={<InfoIcon className="h-4 w-4 text-brand-primary" />}
							tooltipContent={<p>Get a unique username by verifying your profile</p>}
						>
							Username
						</Label>
						<Input
							id="username"
							placeholder="username"
							classNames={{ input: "pl-0.5", container: "mb-5 sm:max-w-full" }}
							prefix={
								<span className="p-3">
									<AtSign className="h-4 w-4 text-brand-primary" />
								</span>
							}
							message={errors?.username?.message}
							{...register("username")}
							disabled
						/>
					</div>

					<div className="mb-5">
						<Label>Your Bio</Label>
						<textarea
							id="bio"
							rows={4}
							placeholder="Enter your short bio"
							className="w-full rounded-md border bg-white p-2 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-gray-300"
							{...register("bio")}
						></textarea>
						{errors?.bio?.message && <ErrorLabel>{errors?.bio?.message}</ErrorLabel>}
					</div>

					<div className="mb-5">
						<Label
							showTooltip
							tooltipTrigger={<InfoIcon className="h-4 w-4 text-brand-primary" />}
							tooltipContent={
								<p>
									Select categories you would like to follow. Categories are different sectors
									within proptech like property management, construction, multifamily etc. Following
									categories helps you receive relevant updates and insights from there.
								</p>
							}
						>
							<span>Your Category</span>
						</Label>

						<MultiSelectDropdown
							buttonLabel="Choose a Category"
							items={sortedCategories}
							selectedItems={selectedCategories}
							setActiveItems={(value) => setSelectedCategories(value)}
						/>
						{showCategoriesError ||
							(selectedCategories.length === 0 && (
								<ErrorLabel>Please follow at least one category</ErrorLabel>
							))}
					</div>

					<div className="mb-5">
						<Label>
							<span>What do you identify yourself as?</span>
						</Label>

						<SelectDropdown
							buttonLabel="What do you identify yourself as?"
							items={howDoYouIdentifyYourselfOptions}
							selectedItem={howDoYouIdentifyYourself}
							setActiveItems={(value) => setHowDoYouIdentifyYourself(value)}
						/>
						{howDoYouIdentifyYourselfError && (
							<ErrorLabel>Please select atleast one role</ErrorLabel>
						)}
					</div>

					<Input
						id="jobTitle"
						label="Job Title"
						placeholder="Enter your Job Title"
						classNames={{ container: "mb-5 sm:max-w-full" }}
						message={errors?.jobTitle?.message}
						{...register("jobTitle")}
					/>

					<div className="mb-5">
						<Label>
							<span>Location</span>
						</Label>

						<SelectDropdown
							buttonLabel="Your Country"
							items={countries}
							selectedItem={selectedLocation}
							setActiveItems={(value) => setSelectedLocation(value)}
							defaultValue={prefillProfileData.location}
						/>

						{showLocationError && <ErrorLabel>Please select a location</ErrorLabel>}
					</div>

					<Input
						id="company"
						label="Company"
						placeholder="Enter your company name"
						classNames={{ input: "pl-0.5", container: "mb-5 sm:max-w-full" }}
						prefix={
							<span className="p-3">
								<BuildingIcon className="h-4 w-4" />
							</span>
						}
						message={errors?.company?.message}
						{...register("company")}
					/>

					<div className="mb-6">
						<Label
							showTooltip
							tooltipTrigger={<InfoIcon className="h-4 w-4 text-brand-primary" />}
							tooltipContent={
								<p>
									Adding more social media links bumps up your PropTech Influencer score on our
									platform.
								</p>
							}
							className="mb-3"
						>
							<span>Social Links</span>
						</Label>

						<div className="grid grid-cols-2 gap-4">
							<Input
								id="personalWebsite"
								label="Your Website"
								placeholder="youramazingwebsite.com"
								classNames={{ input: "pl-0.5", container: "sm:max-w-full" }}
								prefix={
									<span className="p-3">
										<GlobeIcon className="h-4 w-4" />
									</span>
								}
								message={errors?.socialLinks?.personalWebsite?.message}
								{...register("socialLinks.personalWebsite")}
							/>
							<Input
								id="linkedin"
								label="LinkedIn"
								placeholder="linkedin.com/<profile>"
								classNames={{ input: "pl-0.5", container: "sm:max-w-full" }}
								prefix={
									<span className="p-3">
										<LinkedInIcon className="h-4 w-4" />
									</span>
								}
								message={errors?.socialLinks?.linkedin?.message}
								{...register("socialLinks.linkedin")}
							/>
							<Input
								id="twitter"
								label="Twitter"
								placeholder="twitter.com/@profile"
								classNames={{ input: "pl-0.5", container: "sm:max-w-full" }}
								prefix={
									<span className="p-3">
										<TwitterIcon className="h-4 w-4" />
									</span>
								}
								message={errors?.socialLinks?.twitter?.message}
								{...register("socialLinks.twitter")}
							/>
							<div />

							{errors?.socialLinks?.message && (
								<ErrorLabel>{errors?.socialLinks?.message}</ErrorLabel>
							)}
						</div>
					</div>

					<button
						className="w-full rounded-lg bg-brand-primary py-4 text-sm font-semibold text-white disabled:opacity-50"
						disabled={isLoading}
					>
						{isLoading ? <Loader2Icon className="mx-auto h-5 w-5 animate-spin" /> : "Save"}
					</button>
				</form>
			</div>

			{username && (
				<CongratulationsModal
					isVIP={isVIP}
					username={username}
					profileImage={profileImageUrl}
					link={congratulationsModalLink}
					isOpen={showCongratulationsModal}
					linkText={congratulationsModalLinkText}
					onClose={() => setShowCongratulationsModal((prev) => !prev)}
				/>
			)}
		</div>
	);
};

export default UpdateProfileForm;
